import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSIcon from '@/components/ui/PSIcon';

/**
 * ── /components/ps-button ────────────────────────────────────────────────
 *
 * PSButton across its full surface: all five variants (primary, outline,
 * outlineBrand, destructive, toolbar) each through idle/disabled/loading,
 * the `icon` trailing slot (including how loading steps it aside for the
 * spinner), `iconColor`, `fullWidth`, and the wrapped-label height override —
 * plus a live instance that actually fires a request. The reasoning for each
 * lives in the comment on its own block below; this docstring is just the
 * map, not the argument.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Preview-only wrap-width geometry — narrow enough to force PSButton's long
// label into two lines, which is the only way to exercise its `height="auto"`
// override (see the component's docstring). Real call sites size from content
// or `fullWidth`, never a fixed box like this.
const WRAP_LABEL_WIDTH = 160;

export default function PSButtonPreview() {
  // Drives the live PSButton loading demo — a fake 2s request.
  const [booking, setBooking] = useState(false);

  return (
    <>
      <Head>
        <title>PSButton · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* PSButton — idle, disabled, and loading. Loading is deliberately NOT
            the disabled look: full opacity, the caller's progressive verb
            ("Book Now" → "Booking") + a spinner, and taps ignored. The last one
            is LIVE — tap it to watch a fake 2s request round-trip and confirm a
            second tap during the flight doesn't re-fire. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              idle · disabled (dimmed, no press) · loading (full opacity, no
              press)
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSButton title="Book Now" onPress={() => {}} />
              <PSButton title="Book Now" onPress={() => {}} disabled />
              <PSButton title="Book Now" loading loadingTitle="Booking" />
            </XStack>
          </YStack>
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — tap to fire a 2s request; label flips to the verb and back
            </Text>
            <XStack items="flex-start">
              <PSButton
                title="Confirm"
                loading={booking}
                loadingTitle="Confirming"
                onPress={() => {
                  setBooking(true);
                  setTimeout(() => setBooking(false), 2000);
                }}
              />
            </XStack>
          </YStack>

          {/* The remaining three filled/outlined variants, each through the same
            idle / disabled / loading trio as primary above, so all three can be
            compared state-for-state. `Compare memberships` moves down from the
            first row above — it belongs here, next to its own idle/disabled/
            loading siblings, not stranded alone next to primary. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              outline — transparent, hairline `$brandDark` border:
              delete-account&apos;s &quot;Download all tickets&quot;,
              cancel&apos;s &quot;Keep membership&quot;, Footer&apos;s social
              links
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSButton
                title="Compare memberships"
                variant="outline"
                onPress={() => {}}
              />
              <PSButton
                title="Compare memberships"
                variant="outline"
                onPress={() => {}}
                disabled
              />
              <PSButton
                title="Compare memberships"
                variant="outline"
                loading
                loadingTitle="Comparing"
              />
            </XStack>
          </YStack>
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              outlineBrand — brand-purple text and border: my-memberships&apos;
              &quot;Continue membership&quot; / &quot;Cancel membership&quot;,
              PendingPaymentBanner&apos;s &quot;Continue&quot;
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSButton
                title="Continue membership"
                variant="outlineBrand"
                onPress={() => {}}
              />
              <PSButton
                title="Continue membership"
                variant="outlineBrand"
                onPress={() => {}}
                disabled
              />
              <PSButton
                title="Continue membership"
                variant="outlineBrand"
                loading
                loadingTitle="Continuing"
              />
            </XStack>
          </YStack>
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              destructive — the `$error` fill behind delete-account&apos;s
              confirm step (&quot;Delete my account&quot; →
              &quot;Deleting&quot;) and cancel&apos;s &quot;Cancel
              membership&quot;
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSButton
                title="Delete my account"
                variant="destructive"
                onPress={() => {}}
              />
              <PSButton
                title="Delete my account"
                variant="destructive"
                onPress={() => {}}
                disabled
              />
              <PSButton
                title="Delete my account"
                variant="destructive"
                loading
                loadingTitle="Deleting"
              />
            </XStack>
          </YStack>

          {/* toolbar — home's logged-out headerRight ("LOGIN", see
            HomePageHeader.tsx's HomeHeaderRight). That header sits transparent
            over the hero photo, so the pink hairline + pink label need a dark
            backdrop to read at all; `$brandDark` below is a STAND-IN for that
            photo, not a surface this variant is ever really drawn against.
            (The component's own docstring calls the real target
            "darkToolbar" — no token by that name exists anywhere in
            constants/tokens.ts, so we don't repeat it here.) */}
          <YStack bg="$color.brandDark" p="$space.xl" gap="$lg" rounded="$base">
            <Text fontFamily="$body" fontSize="$small" color="$textWhiteMuted">
              toolbar — home&apos;s logged-out header CTA; shown here on
              `$brandDark` standing in for the transparent header&apos;s
              hero-image backdrop, not a real surface it draws on
            </Text>
            <PSButton title="LOGIN" variant="toolbar" onPress={() => {}} />
          </YStack>

          {/* icon — the trailing-element slot: PSUpsellBanner / AccessPassShowreel's
            arrow-forward CTA, and Footer's brand-logo outline buttons. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              icon — trailing arrow on a primary CTA vs a brand logo on an
              outline button (Footer&apos;s WhatsApp/Instagram links)
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSButton
                title="Start free trial"
                onPress={() => {}}
                icon={
                  <PSIcon name="arrow-forward" color="$textWhite" size={18} />
                }
              />
              <PSButton
                title="Find out first on WhatsApp"
                variant="outline"
                icon={<PSIcon name="logo-whatsapp" />}
                onPress={() => {}}
              />
            </XStack>
          </YStack>
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              same button, idle vs loading — the spinner takes the arrow&apos;s
              trailing slot, so the arrow steps aside instead of sitting beside
              a second spinner
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSButton
                title="Start free trial"
                onPress={() => {}}
                icon={
                  <PSIcon name="arrow-forward" color="$textWhite" size={18} />
                }
              />
              <PSButton
                title="Start free trial"
                loading
                loadingTitle="Starting"
                icon={
                  <PSIcon name="arrow-forward" color="$textWhite" size={18} />
                }
              />
            </XStack>
          </YStack>

          {/* iconColor only moves the spinner (see PSButton's docstring on the
            getTokenValue bridge), so it only shows up on a LOADING button.
            Default is textWhite — invisible-ish against outline's near-white
            fill — which is exactly why delete-account's "Download all
            tickets" (the only real iconColor="textDark" call site) needs the
            override. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              iconColor — default `textWhite` spinner vs `textDark`
              (delete-account&apos;s &quot;Download all tickets&quot;); only
              visible while loading
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSButton
                title="Download all tickets"
                variant="outline"
                loading
                loadingTitle="Preparing"
              />
              <PSButton
                title="Download all tickets"
                variant="outline"
                loading
                loadingTitle="Preparing"
              />
            </XStack>
          </YStack>

          {/* fullWidth stretches the BUTTON to its container's width — it does
            nothing for the container itself. This page's outer YStack is
            `items="flex-start"` (content-width by default), so the wrapper
            below opts into `alignSelf="stretch"` — NOT `flex`, which is the
            main-axis prop and wouldn't even apply on a YStack's own cross
            axis the way it's tempting to reach for — just to give the button
            something to stretch against, the way the event and
            membership confirm pages' booking bars do implicitly (their
            parent already stretches). */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              fullWidth — stretches to fill its container (booking bars when
              there&apos;s no price beside the button); wrapped here in an
              `alignSelf=&quot;stretch&quot;` box so there&apos;s a width to
              fill at all
            </Text>
            <YStack alignSelf="stretch">
              <PSButton title="Book Now" fullWidth onPress={() => {}} />
            </YStack>
          </YStack>

          {/* Tamagui's Button defaults to a fixed height that clips a wrapped
            2-line label — worse on web, where that height is a hard CSS
            value rather than just a layout hint. PSButton overrides it with
            `height="auto"` (see its docstring); this is the one preview that
            actually forces the wrap, in a fixed-width box narrow enough that
            the label has nowhere to go but down. Watch that the button GROWS
            to fit both lines instead of clipping the second — most likely to
            regress on web. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              long label wraps to two lines — the button should grow to fit, not
              clip (watch this one on web especially)
            </Text>
            <YStack width={WRAP_LABEL_WIDTH}>
              <PSButton
                title="Confirm and continue to secure checkout"
                onPress={() => {}}
              />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
